import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  Activity,
  Zap,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Wifi,
  WifiOff,
  Loader2,
  ExternalLink,
  Shield,
  Server
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';
import { formatDistanceToNow } from 'date-fns';

type WebhookStatus = {
  topic: string;
  registered: boolean;
  webhookId: string | null;
  address: string | null;
  createdAt: string | null;
};

type ShopWebhookStatus = {
  shopDomain: string;
  webhooks: WebhookStatus[];
  allRegistered: boolean;
  webhookEndpoint: string;
  totalRegistered: number;
  error?: string;
};

type WebhookHealth = {
  overall_health: 'healthy' | 'warning' | 'critical' | 'unknown';
  last_webhook_received_at: string | null;
  last_webhook_test_at: string | null;
  webhooks_verified: boolean;
  per_topic_last_received: Record<string, string>;
  logs_summary: {
    total_count?: number;
    success_count?: number;
    error_count?: number;
    avg_processing_time_ms?: number;
    topics?: Record<string, {
      count: number;
      last_received: string;
      success_rate: number;
    }>;
  };
};

type WebhookLog = {
  id: string;
  topic: string;
  received_at: string;
  processing_time_ms: number;
  status: 'success' | 'error';
  error_message: string | null;
  shop_domain: string;
};

type TestResult = {
  success: boolean;
  endpoint: string;
  reachable: boolean;
  responseTime: number;
  status?: number;
  error?: string;
  testedAt: string;
};

const TOPIC_LABELS: Record<string, string> = {
  'inventory_levels/update': 'Inventory Updates',
  'products/update': 'Product Updates',
  'products/delete': 'Product Deletions',
  'orders/create': 'New Orders',
  'orders/updated': 'Order Updates',
  'orders/cancelled': 'Order Cancellations',
  'app/uninstalled': 'App Uninstalled',
};

const TOPIC_ICONS: Record<string, string> = {
  'inventory_levels/update': 'package',
  'products/update': 'box',
  'products/delete': 'trash',
  'orders/create': 'shopping-cart',
  'orders/updated': 'edit',
  'orders/cancelled': 'x-circle',
  'app/uninstalled': 'power',
};

export function WebhookStatusDashboard() {
  const { token } = useAuth();
  const [webhookStatus, setWebhookStatus] = useState<ShopWebhookStatus[] | null>(null);
  const [webhookHealth, setWebhookHealth] = useState<WebhookHealth | null>(null);
  const [recentLogs, setRecentLogs] = useState<WebhookLog[]>([]);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [logsExpanded, setLogsExpanded] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);
  const [selectedTopicFilter, setSelectedTopicFilter] = useState<string | null>(null);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  const fetchWebhookStatus = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/shopify-oauth/webhook-status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(30000),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWebhookStatus(data.shops || []);
      }
    } catch (err) {
      console.error('Failed to fetch webhook status:', err);
    }
  }, [token, supabaseUrl]);

  const fetchWebhookHealth = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/shopify-oauth/webhook-health`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(30000),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWebhookHealth(data.health);
        setRecentLogs(data.recentLogs || []);
      }
    } catch (err) {
      console.error('Failed to fetch webhook health:', err);
    }
  }, [token, supabaseUrl]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([fetchWebhookStatus(), fetchWebhookHealth()]);
    setRefreshing(false);
  }, [fetchWebhookStatus, fetchWebhookHealth]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchWebhookStatus(), fetchWebhookHealth()]);
      setLoading(false);
    };
    loadData();
  }, [fetchWebhookStatus, fetchWebhookHealth]);

  const handleTestConnection = async () => {
    if (!token) return;

    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(
        `${supabaseUrl}/functions/v1/shopify-oauth/test-webhook-endpoint`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          signal: AbortSignal.timeout(30000),
        }
      );

      const data = await response.json();
      setTestResult(data);
      await fetchWebhookHealth();
    } catch (err) {
      setTestResult({
        success: false,
        endpoint: `${supabaseUrl}/functions/v1/shopify-webhook`,
        reachable: false,
        responseTime: 0,
        error: err instanceof Error ? err.message : 'Connection failed',
        testedAt: new Date().toISOString(),
      });
    } finally {
      setTesting(false);
    }
  };

  const copyEndpoint = () => {
    const endpoint = `${supabaseUrl}/functions/v1/shopify-webhook`;
    navigator.clipboard.writeText(endpoint);
    setCopiedEndpoint(true);
    setTimeout(() => setCopiedEndpoint(false), 2000);
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'text-green-600 dark:text-green-400';
      case 'warning':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'critical':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-500 dark:text-gray-400';
    }
  };

  const getHealthBg = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800';
      case 'warning':
        return 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800';
      case 'critical':
        return 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800';
      default:
        return 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
    }
  };

  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />;
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500 dark:text-gray-400" />;
    }
  };

  const getHealthLabel = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'All Systems Operational';
      case 'warning':
        return 'Degraded Performance';
      case 'critical':
        return 'Service Disruption';
      default:
        return 'Status Unknown';
    }
  };

  const filteredLogs = selectedTopicFilter
    ? recentLogs.filter(log => log.topic === selectedTopicFilter)
    : recentLogs;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl" />
        <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  const shop = webhookStatus?.[0];
  const overallHealth = webhookHealth?.overall_health || 'unknown';

  return (
    <div className="space-y-6">
      {/* Header with Health Status */}
      <div className={`p-5 rounded-xl border ${getHealthBg(overallHealth)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${
              overallHealth === 'healthy' ? 'bg-green-500' :
              overallHealth === 'warning' ? 'bg-yellow-500' :
              overallHealth === 'critical' ? 'bg-red-500' : 'bg-gray-500'
            }`}>
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                {getHealthIcon(overallHealth)}
                <h3 className={`text-lg font-semibold ${getHealthColor(overallHealth)}`}>
                  {getHealthLabel(overallHealth)}
                </h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {shop?.shopDomain ? (
                  <>Connected to <span className="font-medium">{shop.shopDomain}</span></>
                ) : (
                  'No store connected'
                )}
              </p>
            </div>
          </div>
          <Button
            onClick={refreshAll}
            variant="secondary"
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Quick Stats */}
        {webhookHealth?.logs_summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {webhookHealth.logs_summary.total_count || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Events (48h)</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {webhookHealth.logs_summary.success_count || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Successful</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {webhookHealth.logs_summary.error_count || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Errors</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                {webhookHealth.logs_summary.avg_processing_time_ms?.toFixed(0) || '—'}
                <span className="text-sm font-normal">ms</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Avg. Response</p>
            </div>
          </div>
        )}
      </div>

      {/* Webhook Registration Status */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <Zap className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Webhook Registration
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {shop?.allRegistered
                    ? 'All webhooks registered successfully'
                    : 'Some webhooks may need registration'}
                </p>
              </div>
            </div>
            {shop && (
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                shop.allRegistered
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              }`}>
                {shop.webhooks.filter(w => w.registered).length}/{shop.webhooks.length} Active
              </span>
            )}
          </div>
        </div>

        {shop && (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {shop.webhooks.map((webhook) => {
              const topicStats = webhookHealth?.logs_summary?.topics?.[webhook.topic];
              const lastReceived = webhookHealth?.per_topic_last_received?.[webhook.topic];

              return (
                <div
                  key={webhook.topic}
                  className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {webhook.registered ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {TOPIC_LABELS[webhook.topic] || webhook.topic}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {webhook.topic}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {topicStats && (
                      <div className="text-right hidden sm:block">
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {topicStats.count} events
                        </p>
                        <p className="text-xs text-gray-500">
                          {topicStats.success_rate}% success
                        </p>
                      </div>
                    )}
                    {lastReceived && (
                      <div className="text-right">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Last received
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {formatDistanceToNow(new Date(lastReceived), { addSuffix: true })}
                        </p>
                      </div>
                    )}
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      webhook.registered
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {webhook.registered ? 'Registered' : 'Missing'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!shop && (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Server className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No Shopify store connected</p>
            <p className="text-sm mt-1">Connect a store to see webhook status</p>
          </div>
        )}
      </div>

      {/* Connection Test */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Connection Test
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Verify that your webhook endpoint is accessible and responding correctly
              </p>
            </div>
          </div>
          <Button
            onClick={handleTestConnection}
            variant="primary"
            disabled={testing}
            className="flex items-center gap-2 shrink-0"
          >
            {testing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Wifi className="h-4 w-4" />
                Test Connection
              </>
            )}
          </Button>
        </div>

        {/* Endpoint URL */}
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Webhook Endpoint</p>
              <code className="text-sm text-gray-800 dark:text-gray-200 font-mono truncate block">
                {supabaseUrl}/functions/v1/shopify-webhook
              </code>
            </div>
            <button
              onClick={copyEndpoint}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Copy endpoint URL"
            >
              {copiedEndpoint ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4 text-gray-500" />
              )}
            </button>
          </div>
        </div>

        {/* Test Results */}
        {testResult && (
          <div className={`mt-4 p-4 rounded-lg border ${
            testResult.reachable
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}>
            <div className="flex items-start gap-3">
              {testResult.reachable ? (
                <Wifi className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <WifiOff className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <h4 className={`font-medium ${
                  testResult.reachable
                    ? 'text-green-800 dark:text-green-300'
                    : 'text-red-800 dark:text-red-300'
                }`}>
                  {testResult.reachable ? 'Endpoint is Reachable' : 'Connection Failed'}
                </h4>
                <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Response Time:</span>
                    <span className={`ml-2 font-medium ${
                      testResult.reachable ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {testResult.responseTime}ms
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Status Code:</span>
                    <span className={`ml-2 font-medium ${
                      testResult.status === 200 ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {testResult.status || 'N/A'}
                    </span>
                  </div>
                </div>
                {testResult.error && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    Error: {testResult.error}
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Tested {formatDistanceToNow(new Date(testResult.testedAt), { addSuffix: true })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Last Test Info */}
        {webhookHealth?.last_webhook_test_at && !testResult && (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Last tested: {formatDistanceToNow(new Date(webhookHealth.last_webhook_test_at), { addSuffix: true })}
          </p>
        )}
      </div>

      {/* Activity Log */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <button
          onClick={() => setLogsExpanded(!logsExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Activity Log
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {recentLogs.length} events in the last 48 hours
              </p>
            </div>
          </div>
          {logsExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </button>

        {logsExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700">
            {/* Filter */}
            <div className="p-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <select
                value={selectedTopicFilter || ''}
                onChange={(e) => setSelectedTopicFilter(e.target.value || null)}
                className="text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">All Topics</option>
                {Object.entries(TOPIC_LABELS).map(([topic, label]) => (
                  <option key={topic} value={topic}>{label}</option>
                ))}
              </select>
            </div>

            {/* Logs Table */}
            {filteredLogs.length > 0 ? (
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Topic
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {TOPIC_LABELS[log.topic] || log.topic}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {formatDistanceToNow(new Date(log.received_at), { addSuffix: true })}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {log.processing_time_ms}ms
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
                            log.status === 'success'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {log.status === 'success' ? (
                              <CheckCircle className="h-3 w-3" />
                            ) : (
                              <XCircle className="h-3 w-3" />
                            )}
                            {log.status === 'success' ? 'Success' : 'Error'}
                          </span>
                          {log.error_message && (
                            <p className="text-xs text-red-500 mt-1 truncate max-w-[200px]" title={log.error_message}>
                              {log.error_message}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No webhook activity recorded</p>
                <p className="text-sm mt-1">Events will appear here once webhooks start firing</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
